import { Component, Input, OnInit } from '@angular/core';
import { ProfileService } from '../../profile.service';
import { User } from '../../types/user.model';
import {
  MessagingPreferenceColumn,
  MessagingPreferencesResponse,
} from '../../types/messaging-preferences.model';

export interface PreferenceCategoryGroup {
  categoryKey: string;
  categoryLabel: string;
  columns: MessagingPreferenceColumn[];
}

@Component({
  selector: 'mmb-web-app-step-notification',
  templateUrl: './step-notification.component.html',
  styleUrls: ['./step-notification.component.scss'],
})
export class StepNotificationComponent implements OnInit {
  @Input() user: User;

  /** Column definitions and current values from API (with descriptions). */
  data: MessagingPreferencesResponse | null = null;
  /** Columns grouped by category for display. */
  categoryGroups: PreferenceCategoryGroup[] = [];
  /** Current preference values (slug -> enabled). Updated optimistically, then synced via PUT. */
  preferences: Record<string, boolean> = {};
  isLoading = true;
  openPanels: string[] = [];

  constructor(private profileService: ProfileService) {}

  ngOnInit(): void {
    this._loadPreferences();
  }

  togglePanel(categoryKey: string): void {
    const index = this.openPanels.indexOf(categoryKey);
    if (index > -1) {
      this.openPanels.splice(index, 1);
    } else {
      this.openPanels.push(categoryKey);
    }
  }

  isPanelOpen(categoryKey: string): boolean {
    return this.openPanels.includes(categoryKey);
  }

  isSelected(column: MessagingPreferenceColumn): boolean {
    return !!this.preferences[column.id];
  }

  onChange(column: MessagingPreferenceColumn, ev: Event): void {
    const checked = (ev.target as HTMLInputElement).checked;
    const slug = column.id;
    this.preferences = { ...this.preferences, [slug]: checked };
    this._savePreferences(this.preferences);
  }

  private _loadPreferences(): void {
    this.isLoading = true;
    this.profileService.getMessagingPreferences().subscribe({
      next: (response) => {
        this.data = response;
        this.preferences = { ...(response.preferences || {}) };
        this.categoryGroups = this._groupColumnsByCategory(response.columns || []);
        this.openPanels = this.categoryGroups.map((g) => g.categoryKey);
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  private _groupColumnsByCategory(
    columns: MessagingPreferenceColumn[]
  ): PreferenceCategoryGroup[] {
    const byCategory = new Map<string, MessagingPreferenceColumn[]>();
    const categoryLabels = new Map<string, string>();

    for (const col of columns) {
      const key = col.category || 'other';
      if (!byCategory.has(key)) {
        byCategory.set(key, []);
        categoryLabels.set(
          key,
          col.category_label || key.charAt(0).toUpperCase() + key.slice(1)
        );
      }
      byCategory.get(key)!.push(col);
    }

    return Array.from(byCategory.entries()).map(([categoryKey, cols]) => ({
      categoryKey,
      categoryLabel: categoryLabels.get(categoryKey) || categoryKey,
      columns: cols,
    }));
  }

  private _savePreferences(preferences: Record<string, boolean>): void {
    this.profileService.updateMessagingPreferences({ preferences }).subscribe({
      error: () => {},
    });
  }
}
